import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, CardContent, SinglePick } from '@duck/ui'
import { Mail } from 'lucide-react'
import { apis, useTar } from '@/modules'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useTasks } from '@/hooks/useTasks'
import { toast } from 'sonner'
import { usePersons, personsQueryKeys } from '@/hooks/usePersons'
import { useCohort } from '@/hooks/useCohorts'
import { useQueryClient } from '@tanstack/react-query'
import { StandardLoader } from '@/components/StandardLoader'
import { AssignmentDateTimePicker } from './AssignmentDateTimePicker'
import { PartnerService } from '@duck/api-definitions'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'

const MAX_PERSON_NAMES_TO_SHOW = 3

const combineDateAndTime = (date: Date | undefined, time: string | undefined): Date | undefined => {
  if (!date) return undefined

  // If a time isn't provided, treat it as start-of-day. This keeps comparisons
  // deterministic and forces explicit time selection for same-day ordering.
  const [hoursStr, minutesStr] = (time ?? "00:00").split(":")
  const hours = Number(hoursStr)
  const minutes = Number(minutesStr)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined

  const next = new Date(date)
  next.setHours(hours, minutes, 0, 0)
  return next
}

interface SendAssignmentProps {
  cohortId?: string
  personIds?: string[]
  assignmentId?: string
  sendInvitationEmails?: boolean
  onClose?: () => void
  onSend?: () => void
}

const parseDateTime = (isoString: string | undefined | null): { date: Date | undefined; time: string | undefined } => {
  if (!isoString) return { date: undefined, time: undefined }
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return { date: undefined, time: undefined }
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return { date, time: `${hours}:${minutes}` }
}

export const SendAssignment: React.FC<SendAssignmentProps> = ({ cohortId, personIds: initialPersonIds, assignmentId, sendInvitationEmails, onClose, onSend }) => {
  const { tasks } = useTasks()
  const { t } = useTar()
  const { t: tP } = usePartnerTar()
  const queryClient = useQueryClient()

  const isEditMode = !!assignmentId

  const [assignmentData, setAssignmentData] = useState<{
    cohortId: string
    personIds: string[]
    ownerId: string
    scope: PartnerService.AssignmentScope
    status: PartnerService.AssignmentStatus
  } | null>(null)
  const [personIds, setPersonIds] = useState<string[]>(initialPersonIds || [])
  const { persons, refetch: refetchPersons } = usePersons(personIds)
  const { refetch: refetchCohort } = useCohort(cohortId)
  const { getOrgSetting } = useOrganizationSettings()

  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined)
  const [startEnabled, setStartEnabled] = useState(true)
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [startTime, setStartTime] = useState<string | undefined>('00:00')

  const [dueEnabled, setDueEnabled] = useState(false)
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined)
  const [dueTime, setDueTime] = useState<string | undefined>('00:00')

  const [overdueEnabled, setOverdueEnabled] = useState(false)
  const [overdueDate, setOverdueDate] = useState<Date | undefined>(undefined)
  const [overdueTime, setOverdueTime] = useState<string | undefined>('00:00')

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false)

  // Fetch assignment data when in edit mode
  useEffect(() => {
    if (assignmentId && isEditMode) {
      setIsLoadingAssignment(true)
      apis.partnerService.assignmentsAssignmentIdGet(assignmentId)
        .then((response) => {
          const assignment = response.data.data
          if (assignment) {
            // Extract personIds from assignment (assuming it has personIds or we need to get them from the assignment)
            // For now, we'll use the personIds from the assignment if available
            // The assignment response might not include personIds directly, so we might need to handle this differently
            // But for now, let's assume we have them from initialPersonIds or we'll need to fetch them separately
            const finalCohortId = cohortId || assignment.cohortId || ''
            const finalPersonIds = initialPersonIds || assignment.personIds || []

            setAssignmentData({
              cohortId: finalCohortId,
              personIds: finalPersonIds,
              ownerId: assignment.ownerId,
              scope: assignment.scope,
              status: assignment.status
            })
            setPersonIds(finalPersonIds)
            setSelectedTaskId(assignment.taskId)

            // Parse dates
            const start = parseDateTime(assignment.startDate)
            const due = parseDateTime(assignment.dueDate)
            const overdue = parseDateTime(assignment.overdueDate)

            setStartEnabled(!!assignment.startDate)
            setStartDate(start.date)
            setStartTime(start.time)

            setDueEnabled(!!assignment.dueDate)
            setDueDate(due.date)
            setDueTime(due.time)

            setOverdueEnabled(!!assignment.overdueDate)
            setOverdueDate(overdue.date)
            setOverdueTime(overdue.time)
          }
        })
        .catch((error) => {
          console.error('Failed to load assignment:', error)
          toast.error(t('errors.errorLoadingAssignment'))
        })
        .finally(() => {
          setIsLoadingAssignment(false)
        })
    }
  }, [assignmentId, isEditMode, cohortId, initialPersonIds, t])

  const startAt = startEnabled ? combineDateAndTime(startDate, startTime) : undefined
  const dueAt = dueEnabled ? combineDateAndTime(dueDate, dueTime) : undefined
  const overdueAt = overdueEnabled ? combineDateAndTime(overdueDate, overdueTime) : undefined

  const dueAfterStartError =
    startEnabled && dueEnabled && startAt && dueAt && dueAt <= startAt
      ? t('errors.dueDateMustBeAfterStartDate')
      : undefined

  const overdueAfterDueError =
    dueEnabled && overdueEnabled && dueAt && overdueAt && overdueAt <= dueAt
      ? t('errors.overdueDateMustBeAfterDueDate')
      : undefined

  useEffect(() => {
    if (!startEnabled) {
      setDueEnabled(false)
      setDueDate(undefined)
      setDueTime(undefined)
      setOverdueEnabled(false)
      setOverdueDate(undefined)
      setOverdueTime(undefined)
    }
  }, [startEnabled])

  useEffect(() => {
    if (!dueEnabled) {
      setOverdueEnabled(false)
      setOverdueDate(undefined)
      setOverdueTime(undefined)
    }
  }, [dueEnabled])

  const handleSend = async () => {
    if (!selectedTaskId) {
      toast.error(t('common.selectChallenge'))
      return
    }
    if (dueAfterStartError || overdueAfterDueError) {
      toast.error(t('errors.pleaseFixTimeWindows'))
      return
    }

    const finalCohortId = cohortId || assignmentData?.cohortId
    const finalPersonIds = personIds.length > 0 ? personIds : assignmentData?.personIds || []

    if (!finalCohortId) {
      return
    }
    if (finalPersonIds.length === 0) {
      return
    }

    const invitationEmails = getOrgSetting('invitationEmails') ?? false

    setIsLoading(true)
    try {
      if (isEditMode && assignmentId && assignmentData) {
        // Update existing assignment
        await apis.partnerService.assignmentsAssignmentIdPut(assignmentId, {
          cohortId: finalCohortId,
          taskId: selectedTaskId,
          ownerId: assignmentData.ownerId,
          startDate: startAt?.toISOString(),
          dueDate: dueAt?.toISOString(),
          overdueDate: overdueAt?.toISOString(),
          scope: assignmentData.scope,
          personIds: finalPersonIds,
          status: assignmentData.status,
        })
        toast.success(t('common.assignmentUpdated'))
      } else {
        // Create new assignment
        await apis.partnerService.assignmentsPost({
          cohortId: finalCohortId,
          taskId: selectedTaskId,
          startDate: startAt?.toISOString(),
          dueDate: dueAt?.toISOString(),
          overdueDate: overdueAt?.toISOString(),
          scope: 'people',
          personIds: finalPersonIds,
        })
        toast.success(invitationEmails ? t('partner.assignmentSent') : t('partner.assignmentCreated'))
      }
      await refetchCohort()
      await refetchPersons()
      // Invalidate all persons queries to ensure CohortPage refetches persons data
      await queryClient.invalidateQueries({ queryKey: personsQueryKeys.lists() })
      onSend?.()
    } catch (error) {
      console.error(error)
      toast.error(isEditMode ? t('errors.errorUpdatingAssignment') : t('errors.errorSendingAssignment'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingAssignment) {
    return (
      <div className="flex items-center justify-center py-8">
        <StandardLoader size="md" />
      </div>
    )
  }

  return (
    <form>
      <div className="space-y-4">
        {personIds.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="text-sm font-medium text-muted-foreground">{personIds.length > 1 ? tP('XpeopleCOLON', { count: personIds.length }) : tP('personCOLON')}</div>
            <div className="flex flex-row gap-1">
              {(() => {
                const byId = new Map(persons.map((p) => [p.id, p]))
                return personIds.slice(0, MAX_PERSON_NAMES_TO_SHOW).map((id) => {
                  const p = byId.get(id)
                  const fullName = `${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim()
                  return (
                    <Badge key={id} className="text-sm font-medium" variant="outline">
                      {fullName || 'Unknown'}
                    </Badge>
                  )
                })
              })()}
              {personIds.length > MAX_PERSON_NAMES_TO_SHOW && (
                <>
                  <Badge className="text-sm font-medium" variant="outline">+{personIds.length - MAX_PERSON_NAMES_TO_SHOW}</Badge>
                </>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium text-muted-foreground">{t('challenges.challenge')}:</div>
          <SinglePick
            options={tasks.map(task => ({ label: task.name, value: task.id ?? '' }))}
            selected={selectedTaskId}
            onChange={setSelectedTaskId}
            placeholder={t('common.selectChallenge')}
            pickIfOne
            disabled={isEditMode}
          />
        </div>

        <div className="space-y-4 flex flex-col items-center">
          <div className="flex flex-col gap-4">
            <AssignmentDateTimePicker
              label={t('partner.startDate')}
              enabled={startEnabled}
              onEnabledChange={setStartEnabled}
              date={startDate}
              onDateChange={setStartDate}
              time={startTime}
              onTimeChange={setStartTime}
            />

            <AssignmentDateTimePicker
              label={t('partner.dueDate')}
              canEnable={startEnabled}
              enabled={dueEnabled}
              onEnabledChange={setDueEnabled}
              date={dueDate}
              onDateChange={setDueDate}
              time={dueTime}
              onTimeChange={setDueTime}
              error={dueAfterStartError}
            />

            {/* <AssignmentDateTimePicker
              label={t('partner.overdueDate')}
              canEnable={dueEnabled}
              enabled={overdueEnabled}
              onEnabledChange={setOverdueEnabled}
              date={overdueDate}
              onDateChange={setOverdueDate}
              time={overdueTime}
              onTimeChange={setOverdueTime}
              error={overdueAfterDueError}
            /> */}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {t('common.timezone')}: {Intl.DateTimeFormat().resolvedOptions().timeZone} (GMT{(() => {
            const offset = -new Date().getTimezoneOffset()
            const sign = offset >= 0 ? '+' : '-'
            const hours = Math.floor(Math.abs(offset) / 60)
            const minutes = Math.abs(offset) % 60
            return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          })()})
        </div>
        {sendInvitationEmails && !isEditMode && (
          <Card className="bg-accent/10 border border-accent/20">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Mail className="h-5 w-5 text-accent" />
                </div>
                <p className="text-sm text-accent">
                  {personIds.length === 1
                    ? t('common.thisActionWillSendEmailInvitation')
                    : t('common.thisActionWillSendEmailInvitations', { count: personIds.length })
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {(!sendInvitationEmails || isEditMode) && (
          <Card className="bg-muted/30 border border-muted/60">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">{t('common.noEmailsWillBeSent')}</p>
              </div>
            </CardContent>
          </Card>
        )}
        <div className="flex flex-row justify-between gap-4 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            className="w-36 flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <StandardLoader size="sm" inline />
                <span>{isEditMode ? (t('common.updating') || 'Updating...') : t('common.sending')}</span>
              </>
            ) : (
              isEditMode ? (t('common.update') || 'Update') : t('common.send')
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
